import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { LeadPublic, LeadSlotPublic } from '@workspace/types';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../../contact-agent/components/ContactAgentPageHeader';
import { Button } from '@/components/ui/button';
import { leadsApi } from '../api/leadsApi';
import { formatDateTime24h } from '../utils/dates';
import { formatEventActor, formatEventTitle, formatScoreFactor, slotLabel } from '../utils/labels';

const CONTACT_SLOT_KEYS = ['contacto_nombre', 'empresa', 'contacto_cargo', 'telefono', 'email'];
const MEETING_SLOT_KEYS = ['interes_reunion', 'franja_horaria'];

function slotDisplayValue(slot: LeadSlotPublic): string {
  if (slot.valueNumber !== null) {
    return `${slot.valueNumber}${slot.unit ? ` ${slot.unit}` : ''}`;
  }
  return slot.valueText ?? slot.valueLiteral;
}

function scoreTone(score: number | null): string {
  if (score === null) {
    return 'text-muted-foreground';
  }
  if (score >= 70) {
    return 'text-emerald-600';
  }
  if (score >= 40) {
    return 'text-amber-600';
  }
  return 'text-rose-600';
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-start justify-between gap-3 border-b border-dashed py-1.5 text-sm last:border-b-0'>
      <span className='text-muted-foreground'>{label}</span>
      <span className='text-right font-medium'>{value}</span>
    </div>
  );
}

export function LeadDetailPage() {
  const { leadId = '' } = useParams<{ leadId: string }>();
  const [lead, setLead] = useState<LeadPublic | null>(null);
  const [error, setError] = useState('');
  // Actividad técnica oculta por defecto: el vendedor ve la ficha limpia.
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(() => {
    if (!leadId) {
      return;
    }
    let cancelled = false;
    void leadsApi
      .getLead(leadId)
      .then(data => {
        if (!cancelled) {
          setLead(data);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeSlots = (lead?.slotValues ?? []).filter(slot => !slot.supersededAt);
  const contactSlots = activeSlots.filter(slot => CONTACT_SLOT_KEYS.includes(slot.slotKey));
  const meetingSlots = activeSlots.filter(slot => MEETING_SLOT_KEYS.includes(slot.slotKey));
  const dataSlots = activeSlots.filter(
    slot => !CONTACT_SLOT_KEYS.includes(slot.slotKey) && !MEETING_SLOT_KEYS.includes(slot.slotKey),
  );
  const ruleFactors = (lead?.ruleId ?? '').split('+').filter(Boolean);
  const firstAttribution = (lead?.attributions ?? []).find(item => item.touch === 'FIRST')
    ?? lead?.attributions?.[0];

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title={lead?.companyName || lead?.contactName || 'Detalle de lead'}
        description='Ficha comercial del lead: calificación, contacto y datos relevados.'
        backTo='/admin/leads/board'
      />

      {error ? <p className='text-sm text-destructive'>{error}</p> : null}
      {!lead && !error ? <p className='text-sm text-muted-foreground'>Cargando…</p> : null}

      {lead ? (
        <div className='grid gap-4 lg:grid-cols-2'>
          {lead.summary ? (
            <Card className='rounded-2xl border-primary/30 bg-primary/5 lg:col-span-2'>
              <CardHeader>
                <CardTitle>Resumen comercial</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-relaxed'>{lead.summary}</p>
              </CardContent>
            </Card>
          ) : null}

          <Card className='rounded-2xl'>
            <CardHeader className='flex flex-row items-center justify-between'>
              <CardTitle>Calificación</CardTitle>
              <div className='flex gap-2'>
                <Badge variant='outline'>{lead.status}</Badge>
                <Badge>{lead.qualification}</Badge>
              </div>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex items-baseline gap-2'>
                <span className={`text-5xl font-bold ${scoreTone(lead.score)}`}>{lead.score ?? '—'}</span>
                <span className='text-sm text-muted-foreground'>/ 100</span>
              </div>
              <InfoRow
                label='Vertical'
                value={lead.verticalLabel || lead.verticalResolved || lead.verticalDeclared || '—'}
              />
              <InfoRow label='Canal' value={lead.sourceChannel ?? '—'} />
              <InfoRow label='Resolución' value={lead.verticalResolutionMethod ?? '—'} />
              <InfoRow label='Vertical bloqueada' value={lead.verticalLocked ? 'Sí' : 'No'} />
              <InfoRow label='Route' value={lead.funnelRoute ?? '—'} />
              <InfoRow
                label='Derivado'
                value={lead.handoffAt ? formatDateTime24h(lead.handoffAt) : 'Todavía no'}
              />
              {ruleFactors.length > 0 ? (
                <div>
                  <p className='mb-1 text-sm text-muted-foreground'>Factores del score</p>
                  <div className='flex flex-wrap gap-1'>
                    {ruleFactors.map(factor => (
                      <Badge key={factor} variant='secondary' className='font-normal'>
                        {formatScoreFactor(factor)}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className='rounded-2xl'>
            <CardHeader>
              <CardTitle>Contacto y reunión</CardTitle>
            </CardHeader>
            <CardContent>
              {contactSlots.length === 0 && meetingSlots.length === 0 ? (
                <p className='text-sm text-muted-foreground'>Sin datos de contacto todavía.</p>
              ) : (
                <>
                  {contactSlots.map(slot => (
                    <InfoRow
                      key={`${slot.vertical}-${slot.slotKey}`}
                      label={slotLabel(slot.slotKey)}
                      value={slotDisplayValue(slot)}
                    />
                  ))}
                  {meetingSlots.map(slot => (
                    <InfoRow
                      key={`${slot.vertical}-${slot.slotKey}`}
                      label={slotLabel(slot.slotKey)}
                      value={slotDisplayValue(slot)}
                    />
                  ))}
                </>
              )}
            </CardContent>
          </Card>

          <Card className='rounded-2xl'>
            <CardHeader>
              <CardTitle>Origen y atribución</CardTitle>
            </CardHeader>
            <CardContent>
              {firstAttribution ? (
                <>
                  <InfoRow label='Fuente' value={firstAttribution.source ?? firstAttribution.utmSource ?? '—'} />
                  <InfoRow label='Medio' value={firstAttribution.medium ?? firstAttribution.utmMedium ?? '—'} />
                  <InfoRow label='Campaña' value={firstAttribution.campaignName ?? firstAttribution.utmCampaign ?? firstAttribution.campaignId ?? '—'} />
                  <InfoRow label='Anuncio' value={firstAttribution.adName ?? firstAttribution.adId ?? '—'} />
                  <InfoRow label='Click ID' value={firstAttribution.ctwaClid ?? firstAttribution.gclid ?? firstAttribution.fbclid ?? '—'} />
                </>
              ) : (
                <p className='text-sm text-muted-foreground'>Sin atribución de campaña registrada.</p>
              )}
            </CardContent>
          </Card>

          <Card className='rounded-2xl'>
            <CardHeader>
              <CardTitle>Ruta comercial</CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow label='Vertical' value={lead.verticalLabel || lead.verticalResolved || '—'} />
              <InfoRow label='Método' value={lead.verticalResolutionMethod ?? '—'} />
              <InfoRow label='Confianza' value={lead.verticalResolutionConfidence !== null && lead.verticalResolutionConfidence !== undefined ? `${Math.round(lead.verticalResolutionConfidence * 100)} %` : '—'} />
              <InfoRow label='Bloqueada' value={lead.verticalLocked ? 'Sí' : 'No'} />
              <InfoRow label='Route' value={lead.funnelRoute ?? '—'} />
            </CardContent>
          </Card>

          <Card className='rounded-2xl lg:col-span-2'>
            <CardHeader>
              <CardTitle>Datos relevados</CardTitle>
            </CardHeader>
            <CardContent>
              {dataSlots.length === 0 ? (
                <p className='text-sm text-muted-foreground'>Sin datos relevados todavía.</p>
              ) : (
                <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                  {dataSlots.map(slot => (
                    <div key={`${slot.vertical}-${slot.slotKey}`} className='rounded-lg border p-3 text-sm'>
                      <p className='text-xs uppercase tracking-wide text-muted-foreground'>
                        {slotLabel(slot.slotKey)}
                      </p>
                      <p className='mt-0.5 font-semibold'>{slotDisplayValue(slot)}</p>
                      {slot.valueLiteral && slot.valueLiteral !== slotDisplayValue(slot) ? (
                        <p className='mt-1 text-xs italic text-muted-foreground'>
                          “{slot.valueLiteral}”
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className='rounded-2xl lg:col-span-2'>
            <CardHeader className='flex flex-row items-center justify-between'>
              <CardTitle>Actividad</CardTitle>
              {(lead.events ?? []).length > 0 ? (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setShowTimeline(prev => !prev)}
                >
                  {showTimeline
                    ? 'Ocultar actividad'
                    : `Ver actividad (${(lead.events ?? []).length})`}
                </Button>
              ) : null}
            </CardHeader>
            {showTimeline ? (
              <CardContent className='space-y-2'>
                {(lead.events ?? []).length === 0 ? (
                  <p className='text-sm text-muted-foreground'>Sin actividad registrada.</p>
                ) : (
                  (lead.events ?? []).map(event => (
                    <div key={event.id} className='rounded-lg border px-3 py-2 text-sm'>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='font-medium'>{formatEventTitle(event)}</span>
                        <span className='text-xs text-muted-foreground'>
                          {formatDateTime24h(event.createdAt)}
                        </span>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        {formatEventActor(event.actor)}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            ) : null}
          </Card>
        </div>
      ) : null}
    </ContactAgentPage>
  );
}
